import { ROUTES } from '@/configs/routes';

import type { NoteEntity, NoteSourceType } from './Notes';

export type NoteSourceFilter = 'all' | 'document' | 'analysis' | 'news' | 'manual';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

export const getNoteSourceLabel = (sourceType: NoteSourceType | null): string | null => {
  if (sourceType === 'document') {
    return 'Документ';
  }

  if (sourceType === 'analysis') {
    return 'Анализ';
  }

  if (sourceType === 'news') {
    return 'Новость';
  }

  if (sourceType === 'manual') {
    return 'Общие';
  }

  return null;
};

export const getNoteSourceGoToButtonLabel = (sourceType: NoteSourceType | null): string => {
  if (sourceType === 'document') {
    return 'Перейти к документу';
  }

  if (sourceType === 'analysis') {
    return 'Перейти к анализу';
  }

  return 'Перейти к источнику';
};

export const getNoteSourceColor = (sourceType: NoteSourceType | null): string => {
  if (sourceType === 'document') {
    return 'indigo';
  }

  if (sourceType === 'analysis') {
    return 'grape';
  }

  if (sourceType === 'news') {
    return 'orange';
  }

  if (sourceType === 'manual') {
    return 'gray';
  }

  return 'blue';
};

export const getNoteSourceArtifactName = (note: Pick<NoteEntity, 'artifactName'>): string | null => {
  return note.artifactName ?? null;
};

const getAnalysisTypeIdFromAnchor = (anchor: unknown): number | null => {
  const a = isRecord(anchor) ? anchor : null;

  if (!a) {
    return null;
  }

  return readNumber(a.analysis_type_id) ?? readNumber(a.analysisTypeId);
};

export const buildNoteSourceRoute = ({
  organizationId,
  note,
}: {
  organizationId: string;
  note: Pick<NoteEntity, 'artifactType' | 'artifactId' | 'anchorPageNumber'> & { anchor?: unknown };
}): string | null => {
  if (!note.artifactType || !note.artifactId) {
    return null;
  }

  if (note.artifactType === 'document') {
    const page = note.anchorPageNumber;

    return ROUTES.organization.documents.document.documentPreview.create(
      organizationId,
      note.artifactId,
      page ? String(Math.max(1, Math.floor(page))) : undefined,
    );
  }

  if (note.artifactType === 'analysis') {
    const documentId = note.artifactId;
    const analysisTypeId = getAnalysisTypeIdFromAnchor(note.anchor);

    if (!analysisTypeId) {
      return null;
    }

    return ROUTES.organization.documents.document.analyze.result.create(
      organizationId,
      documentId,
      Math.floor(analysisTypeId),
    );
  }

  if (note.artifactType === 'news') {
    return ROUTES.organization.news.article.create(organizationId, note.artifactId);
  }

  return null;
};
